/**
 * Pure rules for the organic-v2 experiment (docs/FUNNEL-ORGANIC-V2.md). No DOM,
 * no imports — storage, randomness and the clock are injected — so `npm test`
 * can exercise it with node --test (see test/experimentCore.test.cjs).
 */

// ───────────────────────── split assignment ─────────────────────────

/**
 * Parse VITE_ORGANIC_V2_SPLIT (percent of NEW organic visitors sent to
 * organic-v2). Unset / empty / non-numeric → 0, so shipping the code does not
 * start the test. Clamped to 0–100.
 */
export function parseSplitPercent(raw: unknown): number {
  if (raw === undefined || raw === null) return 0
  const s = String(raw).trim()
  if (!s) return 0
  const n = Number(s)
  if (!Number.isFinite(n)) return 0
  return Math.min(100, Math.max(0, n))
}

export type AssignmentSource = 'ref' | 'explicit' | 'stored' | 'enrollment' | 'returning' | 'split' | 'default'

export interface AssignmentInput {
  /** Variant resolved from a known `?ref=` code, else null. */
  refVariant: string | null
  /** Valid `?variant=` value, else null. */
  explicitVariant: string | null
  /** Unexpired, valid stored variant, else null. */
  storedVariant: string | null
  /** How the stored variant was assigned ('split' for the random draw). */
  storedAssignedBy?: string | null
  /** Variant of an unexpired enrollment record (first landing), else null. Only meta-* is used. */
  enrollmentVariant?: string | null
  /** Browser already has funnel state (enrollment record or saved quiz progress). */
  isReturning: boolean
  /** 0–100 (parseSplitPercent). */
  splitPercent: number
  /** Uniform [0, 1). */
  random: () => number
}

export interface AssignmentResult {
  variant: string
  /** Write the result to storage (with `source` as assignedBy). */
  persist: boolean
  source: AssignmentSource
}

/**
 * Variant resolution, in order:
 *   1. `?ref=<code>` (Meta ad links) — unchanged.
 *   2. `?variant=<name>` (QA / preview, incl. organic-v2) — unchanged.
 *   3. A stored variant — unchanged, EXCEPT a split-assigned organic-v2 falls
 *      back to organic while the split is 0 (kill switch: set the env var to 0
 *      and redeploy; the stored value is kept, so raising it again resumes).
 *   3b. No stored variant but the enrollment says meta-* → that Meta variant,
 *      stored (a lost variant key must not drop a Meta visitor into organic).
 *      Organic enrollments never take this path, so the organic split is unchanged.
 *   4. Split 0 → organic, nothing stored (today's behaviour, byte for byte).
 *   5. Returning browser without a stored variant → organic, stored, so an
 *      organic visitor from before the test is never moved into it.
 *   6. Brand-new visitor → random draw, stored once.
 */
export function resolveVariantAssignment(input: AssignmentInput): AssignmentResult {
  if (input.refVariant) return { variant: input.refVariant, persist: true, source: 'ref' }
  if (input.explicitVariant) return { variant: input.explicitVariant, persist: true, source: 'explicit' }

  const split = parseSplitPercent(input.splitPercent)

  if (input.storedVariant) {
    if (input.storedVariant === 'organic-v2' && input.storedAssignedBy === 'split' && split <= 0) {
      return { variant: 'organic', persist: false, source: 'stored' }
    }
    return { variant: input.storedVariant, persist: false, source: 'stored' }
  }

  if (input.enrollmentVariant && input.enrollmentVariant.startsWith('meta-')) {
    return { variant: input.enrollmentVariant, persist: true, source: 'enrollment' }
  }

  if (split <= 0) return { variant: 'organic', persist: false, source: 'default' }
  if (input.isReturning) return { variant: 'organic', persist: true, source: 'returning' }

  const v2 = input.random() * 100 < split
  return { variant: v2 ? 'organic-v2' : 'organic', persist: true, source: 'split' }
}

// ───────────────────────── page-lifetime pin ─────────────────────────

/** What captureVariant() resolved for this page load. */
export interface VariantPin {
  variant: string
  /** JSON of the record that belongs in storage, or null when nothing is stored. */
  record: string | null
}

/**
 * The variant for the rest of the page: the pin, never a re-read of storage.
 * The step list and copy call getVariant() on every render while the step
 * index lives in memory, so a variant that changed mid-page re-indexed the
 * visitor into another list (iOS, Oct 2026: meta-short step 0 → organic step
 * 1 `solution`, no reload — WebKit dropped the origin's localStorage while
 * the page was open). A record that has gone missing is written back so a
 * later reload still finds it. No pin yet (before captureVariant) → fallback.
 */
export function pinnedVariant(
  pin: VariantPin | null,
  storage: { getItem(key: string): string | null; setItem(key: string, value: string): void } | null,
  key: string,
  fallback: () => string,
): string {
  if (!pin) return fallback()
  if (pin.record !== null && storage) {
    try {
      if (storage.getItem(key) === null) storage.setItem(key, pin.record)
    } catch {
      // storage unusable — the pin still holds for this page
    }
  }
  return pin.variant
}

// ───────────────────────── step list ─────────────────────────

/**
 * Web equivalents of the app's `minimal` onboarding cuts (constants/
 * onboardingVariants.ts). Medical steps, testimonial and the conversion tail
 * are never removed.
 */
export const ORGANIC_V2_REMOVED_STEPS: readonly string[] = [
  'diagnosisTimeline',
  'dietKnowledge',
  'struggles',
  'eatingStyle',
  'motivation',
  'commitment',
  'featurePriority',
]

export function organicV2Steps<T extends { key: string }>(steps: readonly T[]): T[] {
  return steps.filter((s) => !ORGANIC_V2_REMOVED_STEPS.includes(s.key))
}

// ───────────────────────── screen-1 dwell ─────────────────────────

export type DwellBucket = '<3s' | '3-10s' | '10-30s' | '30s+'

export function dwellBucket(ms: number): DwellBucket {
  if (!(ms >= 3000)) return '<3s' // also NaN / negative
  if (ms < 10000) return '3-10s'
  if (ms < 30000) return '10-30s'
  return '30s+'
}

export type Screen1Outcome = 'continue' | 'leave'

export interface Screen1ExitParams {
  outcome: Screen1Outcome
  dwell_ms: number
  dwell_bucket: DwellBucket
}

/**
 * One `funnel_screen1_exit` per visit. `alreadySent` / `markSent` persist the
 * once-flag (sessionStorage in the browser) so going back to screen 1 and
 * leaving again does not fire twice.
 */
export function createScreen1ExitTracker(deps: {
  now: () => number
  send: (params: Screen1ExitParams) => void
  alreadySent?: () => boolean
  markSent?: () => void
}) {
  let startedAt: number | null = null
  let fired = false
  const sent = () => fired || (deps.alreadySent ? deps.alreadySent() : false)

  return {
    start() {
      if (startedAt === null) startedAt = deps.now()
    },
    /** Returns true if the event was sent by this call. */
    exit(outcome: Screen1Outcome): boolean {
      if (startedAt === null || sent()) return false
      fired = true
      deps.markSent?.()
      const dwell = Math.max(0, Math.round(deps.now() - startedAt))
      deps.send({ outcome, dwell_ms: dwell, dwell_bucket: dwellBucket(dwell) })
      return true
    },
  }
}
